import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';
import { startViewTransition } from '@theme/utilities';

/**
 * Filter bar that swaps the collection product grid in-place for the products of a
 * subcollection, without navigating away from the current collection page.
 *
 * Each subcollection is a real collection that shares the `collection` template, so we
 * re-render the existing `main-collection` section against the subcollection's URL using
 * the Section Rendering API. That gives correctly paginated, correctly counted products
 * straight from Shopify — no client-side product filtering, which would not scale to the
 * large collections this store has.
 *
 * The address bar is updated with `history.pushState` so the view is shareable and the
 * grid's own pagination keeps working, but no full page load happens on click.
 */
class SubcollectionFilter extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
    this.#setActiveByPath(window.location.pathname);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  /** @returns {HTMLAnchorElement[]} */
  get links() {
    return /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('.subcollection-filter__link')]);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    const link = target instanceof Element ? target.closest('.subcollection-filter__link') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    // Let the browser handle new-tab / middle clicks etc. normally.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    const targetUrl = new URL(link.href, window.location.href);
    const alreadyHere = link.getAttribute('aria-current') === 'true' && targetUrl.pathname === window.location.pathname;
    if (alreadyHere) return;

    this.#navigate(targetUrl, true);
  };

  #onPopState = () => {
    // Only react to history entries that belong to this filter bar.
    const path = window.location.pathname;
    const known = this.links.some((link) => new URL(link.href, window.location.href).pathname === path);
    if (!known) return;

    this.#navigate(new URL(window.location.href), false);
  };

  /**
   * Renders the product grid for the given collection URL in place.
   *
   * @param {URL} targetUrl - The collection (or subcollection) URL to show.
   * @param {boolean} push - Whether to push a new history entry (true on click, false on back/forward).
   */
  async #navigate(targetUrl, push) {
    const resultsList = document.querySelector('results-list[section-id]');
    const sectionId = resultsList?.getAttribute('section-id');

    // No product grid found, or the grid isn't the section type we know how to render.
    // Degrade gracefully to a normal navigation.
    if (!sectionId) {
      if (push) window.location.assign(targetUrl.href);
      return;
    }

    this.#setActiveByPath(targetUrl.pathname);

    // Validate the section can actually be rendered for this collection before we commit
    // to an in-place swap. A subcollection on a different template won't contain this
    // section id, in which case we navigate instead of showing an empty grid.
    const useCache = !window.Shopify?.designMode;
    let html = null;
    try {
      html = await sectionRenderer.getSectionHTML(sectionId, useCache, new URL(targetUrl));
    } catch (error) {
      html = null;
    }

    if (!html || !html.includes(`shopify-section-${sectionId}`)) {
      if (push) window.location.assign(targetUrl.href);
      return;
    }

    if (push && targetUrl.href !== window.location.href) {
      history.pushState({ subcollectionFilter: true }, '', targetUrl.href);
    }

    // Reset the paginated list's cached pages so infinite scroll (when enabled) refetches
    // against the new URL. Harmless when numbered pagination is used.
    document.dispatchEvent(new FilterUpdateEvent(new URLSearchParams()));

    await startViewTransition(
      () => sectionRenderer.renderSection(sectionId, { url: new URL(targetUrl) }),
      ['product-grid']
    );

    if (push) {
      const wrapper = this.closest('.shopify-section') ?? this;
      if (wrapper.getBoundingClientRect().top < 0) {
        wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  /**
   * Highlights the link whose URL matches the given path, falling back to the "All" link.
   *
   * @param {string} path
   */
  #setActiveByPath(path) {
    let active = this.links.find((link) => new URL(link.href, window.location.href).pathname === path) ?? null;
    if (!active) active = this.querySelector('[data-subcol-all]');

    for (const link of this.links) {
      link.setAttribute('aria-current', link === active ? 'true' : 'false');
    }
  }
}

if (!customElements.get('subcollection-filter')) {
  customElements.define('subcollection-filter', SubcollectionFilter);
}
